import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PageWrapper, { PageHero } from "../components/PageWrapper";
import DiplomexaLeadForm from "../components/DiplomexaLeadForm";
import {
  ArrowUpRight, Plane, Globe2, Award, ShieldCheck, Users, Briefcase,
  GraduationCap, MessagesSquare, Building2, FileCheck2, MapPinned,
  Compass, BookOpen, Languages, Trophy, ScrollText, ClipboardCheck,
  HeartHandshake, Sparkles, CheckCircle2, Network, Layers3,
} from "lucide-react";

const DIPLOMEXA_URL = "https://diplomexa.ai";
const FACTSKOOT_URL = "https://factskoot.com";

/* ===================== CONTENT ===================== */

const SIGNATURE_PROGRAMS = [
  {
    id: "domestic",
    eyebrow: "01 / Programme",
    title: "Domestic Leadership Journey",
    description:
      "A multi-day in-country leadership immersion combining MUN simulations, leadership workshops, and institutional visits in flagship Indian and Gulf cities.",
    audience: "Schools, junior colleges, leadership and MUN clubs.",
    inclusions: [
      "Curated MUN / debate simulation",
      "Leadership and policy workshops",
      "Institutional and university visits",
      "Accommodation and ground logistics",
      "Faculty coordinator support",
      "Participation certificates",
    ],
    icon: Building2,
  },
  {
    id: "international",
    eyebrow: "02 / Programme",
    title: "International MUN & Conference Journey",
    description:
      "A flagship international delegation to a global MUN, youth parliament, or academic conference — engineered end-to-end from preparation to participation.",
    audience: "High schools, universities, established MUN societies.",
    inclusions: [
      "Conference registration & allocations",
      "Position-paper and research mentoring",
      "Visa, travel, and insurance support",
      "Accommodation and ground transfers",
      "On-ground faculty supervision",
      "International participation certificates",
    ],
    icon: Globe2,
    featured: true,
  },
  {
    id: "custom",
    eyebrow: "03 / Programme",
    title: "Custom Global Leadership Delegation",
    description:
      "A bespoke multi-country journey designed around your institutional learning outcomes — combining conferences, university visits, and cultural exchange.",
    audience: "Universities, education councils, premium school networks.",
    inclusions: [
      "Co-designed multi-city itinerary",
      "MUN / conference + university visit",
      "Industry and policy-institution exposure",
      "Cultural exchange engagements",
      "Branded delegation certificates",
      "Post-event reports and analytics",
    ],
    icon: Layers3,
  },
];

const OFFERINGS = [
  { icon: Plane, title: "International MUN Travel Programs", desc: "Curated MUN delegations to global host cities, end-to-end." },
  { icon: Users, title: "School & College Delegation Management", desc: "Cohort planning, role allocation, faculty coordination." },
  { icon: Globe2, title: "Global Student Conference Participation", desc: "Access to international conferences and summits." },
  { icon: Award, title: "Youth Leadership Journeys", desc: "Structured leadership programmes around real events." },
  { icon: MessagesSquare, title: "Debate & Public Speaking Events", desc: "Curated international debate and speaking competitions." },
  { icon: BookOpen, title: "Pre-Conference Student Preparation", desc: "Research, position papers, briefings led by mentors." },
  { icon: Languages, title: "Country & Committee-Based Learning", desc: "Subject-matter learning aligned to delegation roles." },
  { icon: HeartHandshake, title: "Cultural Exchange Programs", desc: "Peer-to-peer cultural immersion at host institutions." },
  { icon: GraduationCap, title: "University Visit Programs", desc: "Campus visits, faculty interactions, admissions exposure." },
  { icon: Building2, title: "Industry & Institutional Exposure", desc: "Industry visits and policy-institution engagements." },
  { icon: ScrollText, title: "International Certificates & Records", desc: "Verified participation and achievement documentation." },
  { icon: ClipboardCheck, title: "Delegation Coordination", desc: "Operational coordination for schools and colleges." },
  { icon: ShieldCheck, title: "Faculty Coordinator Support", desc: "Trained chaperones and on-ground faculty support." },
  { icon: Briefcase, title: "Travel, Visa, Insurance & Logistics", desc: "Complete travel coordination for student groups." },
  { icon: FileCheck2, title: "Event Registration & Documentation", desc: "Digital registration, credentials, and records." },
  { icon: Trophy, title: "Post-Event Reports & Achievements", desc: "Tracking, reports, and student achievement portfolios." },
];

const USE_CASES = [
  { city: "Dubai", label: "Dubai MUN Programs" },
  { city: "Singapore", label: "Singapore Student Leadership Tours" },
  { city: "Malaysia · Indonesia", label: "Malaysia & Indonesia MUN Delegations" },
  { city: "UAE", label: "UAE Educational Exposure Programs" },
  { city: "Global", label: "International School Conference Trips" },
  { city: "Global", label: "Global Debate Competitions" },
  { city: "Global", label: "University-Hosted Youth Summits" },
  { city: "Global", label: "Educational Tourism with Academic Certification" },
  { city: "Global", label: "Leadership & Diplomacy Bootcamps" },
];

const CREDIBILITY_POINTS = [
  {
    icon: Network,
    title: "Powered by Factskoot",
    body: (
      <>
        Backed by{" "}
        <a
          href={FACTSKOOT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="credibility-factskoot-link"
          className="text-gold hover:underline"
        >
          Factskoot's
        </a>{" "}
        education technology ecosystem.
      </>
    ),
  },
  {
    icon: Globe2,
    body: (
      <>
        Integrated with{" "}
        <Link
          to="/educational-tourism"
          data-testid="credibility-tourism-link"
          className="text-gold hover:underline"
        >
          Destineze's educational travel
        </Link>{" "}
        and international exposure programmes.
      </>
    ),
  },
  {
    icon: GraduationCap,
    body: "Suitable for schools, colleges, universities, MUN clubs and leadership organisations.",
  },
  {
    icon: ClipboardCheck,
    body: "Structured preparation, participation, documentation and post-event reporting.",
  },
  {
    icon: Plane,
    body: "Designed for both domestic and international student delegations.",
  },
];

/* ===================== SEO Helper ===================== */

function useDocumentMeta({ title, description }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const tag = document.querySelector('meta[name="description"]');
    const prevDesc = tag ? tag.getAttribute("content") : null;
    if (tag) {
      tag.setAttribute("content", description);
    } else {
      const newTag = document.createElement("meta");
      newTag.name = "description";
      newTag.content = description;
      document.head.appendChild(newTag);
    }

    return () => {
      document.title = prevTitle;
      if (tag && prevDesc != null) tag.setAttribute("content", prevDesc);
    };
  }, [title, description]);
}

/* ===================== PAGE ===================== */

export default function DiplomexaPage() {
  useDocumentMeta({
    title:
      "Diplomexa Journeys by Destineze | Global MUN, Debate & Leadership Travel Programs",
    description:
      "Diplomexa × Destineze helps schools, colleges and universities plan international MUNs, debates, youth leadership journeys, educational tours, student delegations, university visits and global conference experiences.",
  });

  return (
    <PageWrapper testId="diplomexa-page">
      <PageHero
        eyebrow="Diplomexa × Destineze"
        title="Global MUN, Debate & Leadership Journeys Powered by Diplomexa."
        description="International student conferences, Model United Nations, debates, youth parliament programmes, university exposure visits and cultural learning journeys — planned, managed and delivered by Destineze."
        image="https://images.pexels.com/photos/20733081/pexels-photo-20733081.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      {/* Intro — Diplomexa as the platform layer */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
              The Platform Layer
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy font-light tracking-tight">
              An event-tech engine for the next generation of global leaders.
            </h2>
            <div className="mt-8 flex items-center gap-5 p-6 border border-slate-200 bg-[#F8FAFC]">
              <img
                src="/diplomexa-logo.png"
                alt="Diplomexa"
                data-testid="diplomexa-logo-intro"
                className="h-14 w-14 object-contain"
              />
              <div>
                <div className="font-display text-lg text-navy">Diplomexa</div>
                <div className="text-xs uppercase tracking-[0.22em] text-gold-500 mt-1">
                  Conference OS · MUN OS
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pt-3 space-y-5 text-slate-600 leading-relaxed text-base sm:text-lg">
            <p>
              <span className="text-navy font-semibold">Diplomexa</span> is an
              AI-powered Conference OS and MUN OS that helps institutions
              conduct world-class student leadership events — from school-level
              MUNs to global youth summits.
            </p>
            <p>
              Destineze integrates Diplomexa with educational travel to deliver
              real-world global exposure: conferences, MUNs, youth parliaments,
              debate forums, leadership bootcamps, and international academic
              events.
            </p>
            <p className="font-display text-xl text-navy">
              "Destineze doesn't just take students on trips. Destineze creates
              structured global learning journeys powered by Diplomexa."
            </p>
          </div>
        </div>
      </section>

      {/* ===== Signature Programs ===== */}
      <section
        data-testid="diplomexa-signature-programs"
        className="bg-[#F8FAFC] py-20 sm:py-24"
      >
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
            Signature Programmes
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy font-light tracking-tight max-w-3xl">
            Signature Diplomexa × Destineze Programs.
          </h2>
          <p className="mt-4 max-w-2xl text-slate-600">
            Three flagship engagement formats — pick the one that fits your
            cohort, or design something bespoke with our team.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SIGNATURE_PROGRAMS.map((p) => {
              const featured = !!p.featured;
              return (
                <div
                  key={p.id}
                  data-testid={`diplomexa-program-${p.id}`}
                  className={`card-lift flex flex-col p-7 sm:p-8 ${
                    featured
                      ? "bg-navy text-white border border-gold/40"
                      : "bg-white text-navy border border-slate-200"
                  }`}
                >
                  <div
                    className={`text-[11px] uppercase tracking-[0.22em] ${
                      featured ? "text-gold" : "text-gold-500"
                    }`}
                  >
                    {p.eyebrow}
                  </div>

                  <div
                    className={`mt-5 h-12 w-12 flex items-center justify-center ${
                      featured ? "bg-gold text-navy" : "bg-navy text-gold"
                    }`}
                  >
                    <p.icon size={20} strokeWidth={1.5} />
                  </div>

                  <h3
                    className={`mt-6 font-display text-2xl leading-tight font-light tracking-tight ${
                      featured ? "text-white" : "text-navy"
                    }`}
                  >
                    {p.title}
                  </h3>

                  <p
                    className={`mt-3 text-sm leading-relaxed ${
                      featured ? "text-white/75" : "text-slate-600"
                    }`}
                  >
                    {p.description}
                  </p>

                  <div
                    className={`mt-5 text-[10px] uppercase tracking-[0.22em] ${
                      featured ? "text-gold" : "text-gold-500"
                    }`}
                  >
                    Ideal For
                  </div>
                  <div
                    className={`mt-1 text-sm ${
                      featured ? "text-white/85" : "text-navy/80"
                    }`}
                  >
                    {p.audience}
                  </div>

                  <div
                    className={`mt-5 text-[10px] uppercase tracking-[0.22em] ${
                      featured ? "text-gold" : "text-gold-500"
                    }`}
                  >
                    Inclusions
                  </div>
                  <ul className="mt-2 space-y-1.5 flex-1">
                    {p.inclusions.map((line) => (
                      <li
                        key={line}
                        className={`flex items-start gap-2 text-sm ${
                          featured ? "text-white/85" : "text-slate-700"
                        }`}
                      >
                        <CheckCircle2
                          size={14}
                          className={`mt-1 shrink-0 ${
                            featured ? "text-gold" : "text-gold-500"
                          }`}
                        />
                        {line}
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#diplomexa-delegation-form"
                    data-testid={`diplomexa-program-${p.id}-cta`}
                    className={`mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold ${
                      featured
                        ? "text-gold hover:text-gold-300"
                        : "text-navy hover:text-gold-500"
                    }`}
                  >
                    Plan this programme <ArrowUpRight size={14} />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== Capabilities ===== */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
            Capabilities
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy font-light tracking-tight max-w-3xl">
            What Destineze offers through Diplomexa.
          </h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {OFFERINGS.map((o, i) => (
              <div
                key={o.title}
                data-testid={`diplomexa-offering-${i}`}
                className="card-lift bg-white border border-slate-200 p-6"
              >
                <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center">
                  <o.icon size={18} strokeWidth={1.5} />
                </div>
                <div className="mt-5 font-display text-base text-navy leading-snug">
                  {o.title}
                </div>
                <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {o.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Use cases ===== */}
      <section className="bg-[#F8FAFC] py-20 sm:py-24">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
                Use Cases
              </div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy font-light tracking-tight">
                Built for nine real-world programme types.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                From a single MUN delegation to a multi-city leadership
                bootcamp — every programme is designed with academic intent
                and executed with institutional rigor.
              </p>
              <Link
                to="/educational-tourism"
                data-testid="diplomexa-tourism-backlink"
                className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-semibold text-navy hover:text-gold-500 transition"
              >
                Explore Destineze Educational Tourism <ArrowUpRight size={14} />
              </Link>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {USE_CASES.map((u, i) => (
                <div
                  key={u.label}
                  data-testid={`diplomexa-usecase-${i}`}
                  className="bg-white border border-slate-200 p-5 flex items-start gap-3"
                >
                  <Compass className="text-gold-500 mt-0.5" size={18} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold-500">
                      {u.city}
                    </div>
                    <div className="mt-1 text-navy font-display text-base">
                      {u.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Credibility ===== */}
      <section
        data-testid="diplomexa-credibility"
        className="bg-white py-20 sm:py-24"
      >
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
              Built For
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy font-light tracking-tight">
              Built for Schools, Universities &amp; Global Student Delegations.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              An institutional-grade platform stack with a delegation team that
              treats every cohort like a flagship engagement.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-3">
            {CREDIBILITY_POINTS.map((c, i) => (
              <div
                key={i}
                data-testid={`credibility-point-${i}`}
                className="flex items-start gap-4 p-5 border border-slate-200 bg-white"
              >
                <div className="h-10 w-10 shrink-0 bg-navy text-gold flex items-center justify-center">
                  <c.icon size={18} strokeWidth={1.5} />
                </div>
                <div className="text-slate-700 leading-relaxed pt-1.5">
                  {c.title ? (
                    <div className="font-display text-base text-navy mb-1">
                      {c.title}
                    </div>
                  ) : null}
                  <div className="text-sm">{c.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Positioning band ===== */}
      <section className="bg-navy text-white py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="absolute inset-0 hero-radial opacity-60" />
        <div className="relative max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-3 px-4 py-2 border border-white/15 bg-white/[0.03] text-[11px] uppercase tracking-[0.28em] text-gold">
            <Sparkles size={12} /> Positioning
          </div>
          <h2 className="mt-7 font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.15] tracking-tight">
            Destineze does not just take students on trips.
            <br />
            <span className="text-gold">
              Destineze creates structured global learning journeys powered by
              Diplomexa.
            </span>
          </h2>
        </div>
      </section>

      {/* ===== Closing CTA + Diplomexa-specific form ===== */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
            Engage
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy font-light tracking-tight max-w-3xl">
            Take your students beyond classrooms into global leadership experiences.
          </h2>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={DIPLOMEXA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="diplomexa-cta-visit"
              className="group inline-flex items-center gap-2 bg-navy text-white px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold hover:bg-navy-700 transition"
            >
              Visit diplomexa.ai
              <ArrowUpRight
                size={16}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
            <a
              href="#diplomexa-delegation-form"
              data-testid="diplomexa-cta-delegation"
              className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold transition"
            >
              Plan a Student Delegation
            </a>
            <Link
              to="/educational-tourism"
              data-testid="diplomexa-cta-tourism"
              className="inline-flex items-center gap-2 border border-slate-300 text-navy hover:border-navy px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold transition"
            >
              Educational Tourism →
            </Link>
            <Link
              to="/partners"
              data-testid="diplomexa-cta-partner"
              className="inline-flex items-center gap-2 text-navy hover:text-gold-500 px-3 py-4 text-sm uppercase tracking-[0.18em] font-semibold transition"
            >
              Partner with Destineze →
            </Link>
          </div>

          <div id="diplomexa-delegation-form" className="mt-16 scroll-mt-24">
            <DiplomexaLeadForm />
          </div>
        </div>
      </section>

      {/* ===== Brand strip ===== */}
      <section className="bg-navy text-white py-10 border-t border-white/10">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/diplomexa-logo.png"
              alt="Diplomexa"
              className="h-9 w-9 object-contain bg-white/5 p-1"
            />
            <div className="text-sm text-white/80">
              <span className="text-gold font-semibold">
                Diplomexa × Destineze
              </span>
              <span className="mx-2 text-white/30">—</span>
              Turning educational travel into global leadership experiences.
            </div>
          </div>
          <a
            href={DIPLOMEXA_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="diplomexa-footer-link"
            className="inline-flex items-center gap-2 text-gold hover:underline text-sm uppercase tracking-[0.22em]"
          >
            diplomexa.ai <ArrowUpRight size={14} />
          </a>
        </div>
      </section>
    </PageWrapper>
  );
}
